import { useState, useEffect } from 'react';

/**
 * Hook to detect system DPI scaling and provide compensation factor
 * Detects system resolution scaling (e.g., 150%) separately from browser zoom
 * @returns {Object} { systemScaling: number, zoom: number, compensationFactor: number, scale: number }
 */
export function useBrowserZoom() {
  const [systemScaling, setSystemScaling] = useState(100);
  const [zoom, setZoom] = useState(100);
  const [compensationFactor, setCompensationFactor] = useState(1);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    /**
     * Detect system DPI scaling (e.g., 150% Windows display scaling)
     * This is separate from browser zoom level
     */
    const detectSystemScaling = (): number => {
      // Method 1: Use screen DPI ratio (most accurate for system scaling)
      // This works on Windows and some other systems
      const screen = window.screen as any;
      if (screen.deviceXDPI && screen.logicalXDPI) {
        const scaling = (screen.deviceXDPI / screen.logicalXDPI) * 100;
        return Math.round(scaling);
      }
      
      // Method 2: Use devicePixelRatio to infer system scaling
      // For 150% system scaling on standard displays, DPR is typically 1.5
      // For 100% system scaling, DPR is typically 1.0
      const dpr = window.devicePixelRatio || 1;
      
      // Method 3: Assume 150% if DPR is around 1.5 (common for client systems with 150% scaling)
      // This handles the case where client systems are set to 150% resolution
      if (dpr >= 1.4 && dpr <= 1.6) {
        return 150; // Assume 150% system scaling
      }
      
      // Method 4: For other DPR values, calculate scaling
      // DPR of 2.0 = 200% scaling, DPR of 1.0 = 100% scaling
      if (dpr > 1.6) {
        return Math.round(dpr * 100);
      }
      
      return 100; // Default to 100%
    };

    /**
     * Detect browser zoom level (separate from system scaling)
     */
    const detectBrowserZoom = (): number => {
      if (window.visualViewport) {
        // Calculate zoom based on visual viewport vs window dimensions
        const zoomLevel = (window.outerWidth / window.visualViewport.width) * 100;
        return Math.round(zoomLevel);
      }
      return 100; // Default to 100% if can't detect
    };

    const detectZoom = () => {
      // Detect system scaling (150% resolution setting)
      const detectedSystemScaling = detectSystemScaling();
      
      // Detect browser zoom (user's browser zoom level)
      const detectedBrowserZoom = detectBrowserZoom();
      
      // Use system scaling as primary factor for compensation
      // Browser zoom is typically 100% unless user manually zooms
      const effectiveZoom = detectedSystemScaling;
      
      // Clamp to reasonable range (50% to 200%)
      const clampedZoom = Math.max(50, Math.min(200, effectiveZoom));

      setSystemScaling(detectedSystemScaling);
      setZoom(clampedZoom);

      // Calculate compensation factor
      // For 150% system scaling, we want to scale down by 1/1.5 = 0.6667
      const factor = clampedZoom / 100;
      setCompensationFactor(factor);

      // Calculate scale for CSS zoom property
      // If system scaling is 150%, zoom should be 1/1.5 = 0.6667 to make it appear as 100%
      const scaleValue = 100 / clampedZoom;
      setScale(scaleValue);
    };

    // Initial detection
    detectZoom();

    // Listen for zoom changes
    const handleResize = () => {
      detectZoom();
    };

    // Listen for visual viewport changes (more accurate for zoom)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    // Also listen for orientation changes
    window.addEventListener('orientationchange', handleResize);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return { systemScaling, zoom, compensationFactor, scale };
}

